import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { Button, Card, CardBody, CardTitle, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import * as Yup from 'yup';
import { collegeClearCache } from 'containers/college/collegeDetailsHelper';
import { useHistory } from 'react-router-dom';
import { SMKLinks } from 'utils/links';
import { collegeDetailSelector } from '../../selectors';
import { AdmissionContactPersonType, EMAIL_REGEX, PHONE_REGEX } from '../../../../utils/constants';
import { titleCaseIfExists } from '../../../../utils/helper';
import { userSelector } from '../../../../common/globalComponents/selectors';
import Loader from '../../../../components/Loader';
import TextEditor from '../../../../components/TextEditor';
import useRequest from '../../../../hooks/useRequest';

const stateSelector = createStructuredSelector({
  collegeDetail: collegeDetailSelector,
  user: userSelector,
});

const AdmissionDetails = () => {
  const { user, collegeDetail } = useSelector(stateSelector);

  const dispatch = useDispatch();
  const history = useHistory();

  const getInitialValues = () => {
    const { admissionDetails } = collegeDetail;

    const values = {};

    values.academicYear = admissionDetails?.academicYear || '';
    values.admissionPeriod = !isEmpty(admissionDetails?.admissionPeriod)
      ? admissionDetails?.admissionPeriod[0]?.period
      : '';
    values.contactPerson = !isEmpty(admissionDetails?.contactPerson)
      ? admissionDetails?.contactPerson
      : [
          {
            name: '',
            type: 'Contact Person',
            email: '',
            phone: '',
          },
        ];
    values.pgAdmissionDetails = admissionDetails?.pgAdmissionDetails || '';
    values.ugAdmissionDetails = admissionDetails?.ugAdmissionDetails || '';
    values.collegeBrochureUrl = admissionDetails?.collegeBrochureUrl || '';
    values.admissionBrochureUrl = admissionDetails?.admissionBrochureUrl || '';
    return values;
  };

  const [updateAdmissionDetailsRequest, { loading: updateAdmissionDetailsLoading }] = useRequest({
    url: '/college/admin/edit/updateAdmissionDetails',
    method: 'POST',
    onSuccess: (data) => {
      collegeClearCache({
        urlSlug: collegeDetail?.slug,
        collegeId: collegeDetail?.id,
      });

      window.location.reload();
    },
  });

  const formik = useFormik({
    initialValues: getInitialValues(),
    onSubmit(values) {
      updateAdmissionDetailsRequest({
        data: {
          collegeId: collegeDetail?.id,
          admissionDetails: {
            ...values,
            // filter out empty rows
            contactPerson: values.contactPerson.filter((cp) => cp.type && (cp.name || cp.email || cp.phone)),
            admissionPeriod: [{ board: 'All', level: 'All', period: values.admissionPeriod }],
          },
        },
      });
    },
    validationSchema: Yup.object().shape({
      academicYear: Yup.string().nullable(),
      admissionPeriod: Yup.string().nullable(),
      contactPerson: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().nullable(),
          type: Yup.string().test('type test', 'Please select type', function(val) {
            // NOTE: type required only if any of the below provided
            // eslint-disable-next-line react/no-this-in-sfc
            if (!(this.name || this.email || this.phone)) {
              return true;
            }
            return !!val;
          }),
          email: Yup.string()
            .nullable()
            .matches(EMAIL_REGEX, 'Please provide valid email'),
          phone: Yup.string()
            .nullable()
            .matches(PHONE_REGEX, 'Please provide valid number'),
        })
      ),
      ugAdmissionDetails: Yup.string().nullable(),
      pgAdmissionDetails: Yup.string().nullable(),
      collegeBrochureUrl: Yup.string().nullable(),
      admissionBrochureUrl: Yup.string().nullable(),
    }),
  });

  const handleAddPerson = () => {
    formik.setFieldValue('contactPerson', [
      ...formik.values.contactPerson,
      {
        name: '',
        type: '',
        email: '',
        phone: '',
      },
    ]);
  };

  const handleRemovePerson = (index) => {
    const array = formik.values.contactPerson.slice();
    array.splice(index, 1);
    formik.setFieldValue('contactPerson', array);
  };

  const handleUpdatePerson = (index, key, value) => {
    const array = formik.values.contactPerson.slice();
    array[index] = { ...array[index], [key]: value };
    formik.setFieldValue('contactPerson', array);
  };

  const renderContactPersons = () => {
    const types = Object.entries(AdmissionContactPersonType).map(([k, v]) => ({
      value: v,
      label: titleCaseIfExists(k),
    }));

    return (
      <>
        <CardTitle>Admission Contact Person</CardTitle>

        {formik.values.contactPerson.map((person, i) => (
          <Row key={`person_${i}`}>
            <Col md={6}>
              <FormGroup className="mb-4">
                <Label htmlFor="person_type" className="form-label  ">
                  Type
                </Label>
                <Select
                  id="person_type"
                  options={types}
                  value={types.find((t) => t.value === person.type)}
                  onChange={({ value }) => handleUpdatePerson(i, 'type', value)}
                />
                {!isEmpty(formik.errors.contactPerson) && (
                  <div className="invalid-feedback d-block">{formik.errors.contactPerson[i]?.type}</div>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup className="mb-4">
                <Label htmlFor="person_name" className="form-label  ">
                  Name
                </Label>
                <Input
                  id="person_name"
                  type="text"
                  className="form-control"
                  rows="3"
                  placeholder="Name..."
                  value={person.name}
                  onChange={(e) => handleUpdatePerson(i, 'name', e.target.value)}
                />
                {!isEmpty(formik.errors.contactPerson) && (
                  <div className="invalid-feedback d-block">{formik.errors.contactPerson[i]?.name}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={5}>
              <FormGroup className="mb-4">
                <Label htmlFor="person_phone" className="form-label  ">
                  Phone
                </Label>

                <PhoneInput
                  country="in"
                  isValid={isEmpty(formik.errors.contactPerson) || isEmpty(formik.errors.contactPerson[i]?.phone)}
                  enableSearch
                  preferredCountries={['in', 'us', 'sg', 'uk']}
                  value={person.phone}
                  onChange={(value) => handleUpdatePerson(i, 'phone', value)}
                  countryCodeEditable
                />
                {!isEmpty(formik.errors.contactPerson) && (
                  <div className="invalid-feedback d-block">{formik.errors.contactPerson[i]?.phone}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-4">
                <Label htmlFor="person_email" className="form-label  ">
                  Email
                </Label>
                <Input
                  id="person_email"
                  type="text"
                  className="form-control"
                  rows="3"
                  placeholder="Email..."
                  value={person.email}
                  onChange={(e) => handleUpdatePerson(i, 'email', e.target.value)}
                />
                {!isEmpty(formik.errors.contactPerson) && (
                  <div className="invalid-feedback d-block">{formik.errors.contactPerson[i]?.email}</div>
                )}
              </FormGroup>
            </Col>
            <Col sm={1}>
              <FormGroup className="mb-4">
                <Button color="danger" className="mt-4" onClick={() => handleRemovePerson(i)}>
                  <i className="bx bx-trash" />
                </Button>
              </FormGroup>
            </Col>
          </Row>
        ))}

        <Row>
          <Col>
            <Button className="mt-3 w-100" color="light" onClick={handleAddPerson}>
              <i className="bx bx-plus me-2" />
              Add Person
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Loader isActive={updateAdmissionDetailsLoading} />

      <h5 className="mb-4">Admission Details</h5>

      <Card>
        <CardBody>
          <CardTitle>Academic Year</CardTitle>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-4">
                <Label htmlFor="college_acad_year" className="form-label  ">
                  Academic Year
                </Label>
                <Input
                  id="college_acad_year"
                  type="text"
                  className="form-control"
                  rows="3"
                  placeholder=""
                  invalid={!!(formik.touched.academicYear && formik.errors.academicYear)}
                  {...formik.getFieldProps('academicYear')}
                />
                <FormFeedback>{formik.errors.academicYear}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup className="mb-4">
                <Label htmlFor="college_acad_period" className="form-label  ">
                  Admission Period
                </Label>
                <Input
                  id="college_acad_period"
                  type="text"
                  className="form-control"
                  rows="3"
                  placeholder=""
                  invalid={!!(formik.touched.admissionPeriod && formik.errors.admissionPeriod)}
                  {...formik.getFieldProps('admissionPeriod')}
                />
                <FormFeedback>{formik.errors.admissionPeriod}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <hr className="mb-4" />

          {renderContactPersons()}
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <Col md={12}>
            <FormGroup className="mb-4">
              <Label for="collegeBrochureUrl" className="form-label  ">
                College Brochure URL
              </Label>
              <Input
                id="collegeBrochureUrl"
                type="text"
                className="form-control"
                rows="3"
                placeholder="URL..."
                invalid={!!(formik.touched.collegeBrochureUrl && formik.errors.collegeBrochureUrl)}
                {...formik.getFieldProps('collegeBrochureUrl')}
              />
              <FormFeedback>{formik.errors.collegeBrochureUrl}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup className="mb-4">
              <Label for="admissionBrochureUrl" className="form-label  ">
                Admission Brochure URL
              </Label>
              <Input
                id="admissionBrochureUrl"
                type="text"
                className="form-control"
                rows="3"
                placeholder="URL..."
                invalid={!!(formik.touched.admissionBrochureUrl && formik.errors.admissionBrochureUrl)}
                {...formik.getFieldProps('admissionBrochureUrl')}
              />
              <FormFeedback>{formik.errors.admissionBrochureUrl}</FormFeedback>
            </FormGroup>
          </Col>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <FormGroup className="mb-4">
            <Label htmlFor="collegedesc" className="form-label ">
              Under Graduate Admission Details
            </Label>
            <TextEditor
              simple
              initialValue={formik.values.ugAdmissionDetails}
              onChange={(val) => formik.setFieldValue('ugAdmissionDetails', val)}
            />
          </FormGroup>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <FormGroup className="mb-4">
            <Label htmlFor="collegedesc" className="form-label ">
              Post Graduate Admission Details
            </Label>
            <TextEditor
              simple
              initialValue={formik.values.pgAdmissionDetails}
              onChange={(val) => formik.setFieldValue('pgAdmissionDetails', val)}
            />
          </FormGroup>
        </CardBody>
      </Card>

      {(user?.entityPermissions?.college.admin ||
        (collegeDetail.editable &&
          (collegeDetail?.createdBy?.id === user.id || collegeDetail?.createdBy?.teamLead?.id === user.id))) && (
        <div className="mb-4">
          <Button type="submit" color="primary" className="me-3 ">
            Update Details
          </Button>
          <a
            href="#"
            className="ms-4 text-dark"
            onClick={() => {
              history.replace(SMKLinks.COLLEGE_LISTING);
            }}>
            Cancel
          </a>
        </div>
      )}
    </Form>
  );
};

AdmissionDetails.propTypes = {};

export default AdmissionDetails;
